import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { StyleFunction } from 'leaflet';
import { LegendConfig } from '../map/map.component';

export interface GeoMapCardGeometry {
    organizationId: string;
    year: number;
    layerOrder?: number;
    geom: Geometry;
    showTooltip?: boolean;
    [key: string]: any;
}

@UntilDestroy()
@Component({
    selector: 'geo-map-card',
    templateUrl: './geo-map-card.component.html',
    styleUrls: ['./geo-map-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeoMapCardComponent {
    @Input() title: string = '';
    @Input() mapId: string = '';
    @Input() descriptionTitle?: string;
    @Input() descriptionContent?: string;
    @Input() error: boolean = false;
    @Input() isLoading: boolean = true;
    @Input() cardClass: string = '';

    @Input()
    set geometries(value: GeoMapCardGeometry[]) {
        this._createGeoJson(value ?? []);
    }

    @Input() getStyle: StyleFunction<GeoJsonProperties> = () => ({});
    @Input() getTooltip: (feature: Feature) => string | null = () => '';
    @Input() legendConfig: LegendConfig[] = [];
    @Input() zoomable: boolean = true;
    @Input() downloadable: boolean = true;
    @Input() baseMapUrl: string | null = null;
    @Input() height: string | null = null;
    @Input() center: { lat?: number; lon?: number; zoomLevel?: number } | undefined = undefined;

    @HostBinding('class')
    get hostClasses(): string {
        return this.cardClass;
    }

    public layers: GeoJSON.FeatureCollection[] = [];

    private _createGeoJson(geometries: GeoMapCardGeometry[]): void {
        const layerOrders = Array.from(new Set(geometries.map((g) => g.layerOrder ?? 0))).sort(
            (a, b) => a - b,
        );

        this.layers = layerOrders.map((order) => ({
            type: 'FeatureCollection',
            features: geometries
                .filter((geometry) => (geometry.layerOrder ?? 0) === order)
                .map((geometry) => {
                    const { geom, organizationId, year, ...properties } = geometry;

                    return {
                        type: 'Feature',
                        properties,
                        geometry: geom,
                    } as GeoJSON.Feature;
                }),
        }));
    }
}
